import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import valuesIn from 'lodash/valuesIn';
import { getIn } from 'formik';
import { FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Select, { components } from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { borderRadius, colors, fontSize } from '../../constants/styleConstants';
import { removeWithTranslationProps } from '../../utils/common';
import { withTranslation } from '../../src/i18n';

// dynamic imports
const MenuItem = dynamic(() => import('@material-ui/core/MenuItem'));
const Creatable = dynamic(() => import('react-select/creatable'));
const FormHelperText = dynamic(() => import('@material-ui/core/FormHelperText'));
const TextField = dynamic(() => import('@material-ui/core/TextField'));
const Typography = dynamic(() => import('@material-ui/core/Typography'));
const Chip = dynamic(() => import('@material-ui/core/Chip'));
const Paper = dynamic(() => import('@material-ui/core/Paper'));
const ArrowDropDown = dynamic(() => import('@material-ui/icons/ArrowDropDown'));
const Clear = dynamic(() => import('@material-ui/icons/Clear'));
const ExpandMoreIcon = dynamic(() => import('@material-ui/icons/ExpandMore'));

export const styles = {
  field: {
    position: 'relative',
    marginTop: '0 !important',
    zIndex: 'auto',
  },
  underline: {
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
  },
  root: {
    flexGrow: 1,
    '&::before': {
      display: 'none',
    },
  },
  warningBorderColor: {
    '&:after': {
      transform: 'scaleX(1)',
    },
  },
  disabled: {
    '& .MuiInputBase-input': {
      backgroundColor: `${colors.background.grayLight}!important`,
    },
  },
  selectInput: {
    display: 'flex !important',
    padding: '8px 0px 8px 10px !important',
    lineHeight: '1em !important',
    height: 'auto !important',
    fontSize: `${fontSize.textFieldLabel}px !important`,
    border: '1px solid #b3b3b3 !important',
    borderRadius: borderRadius.fields,
    backgroundColor: `${colors.white} !important`,
  },
  focused: {
    '& > div': {
      padding: '8px 0 8px 12px !important',
    },
  },
  useText: {
    fontWeight: 'bold',
  },
  selectLabel: {
    color: `${colors.title} !important`,
  },
  shrink: {
    lineHeight: 'calc(1em + 2px) !important',
    fontSize: `${fontSize.smallTitle}px !important`,
    transform: 'translate(0, 1.5px) scale(1) !important',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden !important',
    textOverflow: 'ellipsis',
  },
  required: {
    '&:after': {
      content: '*',
      color: colors.primary,
      display: 'inline-block',
    },
  },
  fieldLabelFocused: {
    color: `${colors.title} !important`,
  },
  valueContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    alignItems: 'center',
    overflow: 'hidden',
    '& > div': {
      margin: 0,
      position: 'static',
      '-webkit-transform': 'none',
      transform: 'none',
    },
  },
  multipleValueContainer: {
    flexWrap: 'wrap',
  },
  chip: {
    color: colors.white,
    backgroundColor: colors.black,
  },
  chipFocused: {
    backgroundColor: colors.background.gray,
  },
  placeholder: {
    position: 'absolute',
    left: 13,
    fontSize: fontSize.medium,
    maxWidth: '80%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paper: {
    position: 'absolute',
    zIndex: 999,
    left: 0,
    right: 0,
    '& div': {
      padding: 0,
    },
    '& li': {
      padding: '7px 12px',
    },
  },
  controlIcon: {
    display: 'flex',
    transition: 'color 150ms ease 0s',
    boxSizing: 'border-box',
    cursor: 'pointer',
    color: colors.primary,
    zIndex: 1,
    backgroundColor: colors.white,
    marginRight: 5,
  },
  divider: {
    height: '1em',
  },
  warningColor: {
    color: `${colors.primary} !important`,
  },
  errorColor: {
    color: `${colors.red} !important`,
  },
  formHelperText: {
    position: 'absolute',
    bottom: -18,
    textAlign: 'left',
    minHeight: '1em',
    lineHeight: '1em',
    fontSize: 12,
    marginTop: 10,
  },
  groupedLabel: {
    textAlign: 'left',
    textTransform: 'none',
  },
  withMargin: {
    marginTop: `calc(${fontSize.textFieldLabel}px + 7px) !important`,
  },
  asterisk: {
    color: colors.primary,
    lineHeight: `${fontSize.textFieldLabel}px`,
  },
  error: {
    border: `1px solid ${colors.red} !important`,
    color: colors.red,
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
  },
  icon: {
    color: colors.blue,
  },
  noOptions: {
    display: 'flex',
    padding: '7px 12px',
  },
  staticOption: {
    marginTop: 20,
    marginBottom: 20,
    textAlign: 'center',
  },
};

const useStyles = makeStyles(styles);

function NoOptionsMessage(props) {
  const classes = useStyles();
  const { selectProps, innerProps, children } = props;
  return (
    <>
      {((!selectProps.startAggregations && selectProps.options.length === 0) || selectProps.error) && (
        <Typography
          color="textSecondary"
          className={classnames(selectProps.classes.noOptionsMessage, classes.noOptions)}
          {...innerProps}
        >
          {selectProps.customNoResultText || children}
        </Typography>
      )}
    </>
  );
}

NoOptionsMessage.propTypes = {
  children: PropTypes.node.isRequired,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
};

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} style={{ color: colors.black }} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

function Control(props) {
  const inputSelectProps = get(props, 'selectProps.InputProps');
  const textFieldProps = get(props, 'selectProps.textFieldProps');
  const classes = useStyles();
  return (
    <TextField
      fullWidth
      InputProps={{
        classes: {
          root: classnames(classes.root, {
            [classes.warningBorderColor]: textFieldProps.warning,
            [classes.disabled]: props.selectProps.isDisabled,
          }),
          underline: classes.underline,
          focused: classes.focused,
        },
        inputComponent,
        disableUnderline: props.selectProps.disableUnderline,
        inputProps: {
          className: classnames(classes.selectInput, {
            [classes.disabled]: props.selectProps.isDisabled,
            [classes.error]: textFieldProps.error,
          }),
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
        ...inputSelectProps,
      }}
      InputLabelProps={{
        shrink: true,
        classes: {
          root: classnames(classes.selectLabel, {
            [classes.warningColor]: textFieldProps.warning,
          }),
          asterisk: classes.asterisk,
          focused: classes.fieldLabelFocused,
          shrink: classes.shrink,
        },
        ...textFieldProps.inputLabelProps,
      }}
      label={textFieldProps.label}
      required={textFieldProps.required}
      error={textFieldProps.error}
      onChange={e => props.selectProps.onInputTextChange(e.target.value)}
    />
  );
}

Control.propTypes = {
  children: PropTypes.node.isRequired,
  innerRef: PropTypes.func.isRequired,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
  onInputTextChange: PropTypes.func,
  textFieldProps: PropTypes.shape({
    label: PropTypes.node,
    inputLabelProps: PropTypes.object,
    required: PropTypes.bool,
    error: PropTypes.bool,
    warning: PropTypes.bool,
  }),
};

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      style={{
        fontWeight: props.isSelected ? 800 : 400,
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        fontSize: fontSize.basic,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

Option.propTypes = {
  children: PropTypes.node.isRequired,
  innerRef: PropTypes.func,
  innerProps: PropTypes.object,
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
};

function Placeholder(props) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

Placeholder.propTypes = {
  children: PropTypes.node.isRequired,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
};

function ValueContainer(props) {
  return (
    <div
      className={classnames(props.selectProps.classes.valueContainer, {
        [props.selectProps.classes.multipleValueContainer]: props.isMulti,
      })}
    >
      {props.children}
    </div>
  );
}

ValueContainer.propTypes = {
  children: PropTypes.node.isRequired,
  selectProps: PropTypes.object,
  isMulti: PropTypes.bool,
};

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classnames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
    />
  );
}

MultiValue.propTypes = {
  children: PropTypes.node.isRequired,
  removeProps: PropTypes.object.isRequired,
  isFocused: PropTypes.bool,
  selectProps: PropTypes.object,
};

const ClearIndicator = props => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref} className={props.selectProps.classes.controlIcon}>
      <Clear style={{ fontSize: '1rem' }} />
    </div>
  );
};

ClearIndicator.propTypes = {
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
};

const DropdownIndicator = props => {
  const classes = useStyles();
  const {
    innerProps: { ref, ...restInnerProps },
    selectProps,
    isDisabled,
  } = props;

  const { customIndicator, noIndicator } = selectProps;
  if (isDisabled || noIndicator) {
    return null;
  }

  return (
    <div {...restInnerProps} ref={ref} className={props.selectProps.classes.controlIcon}>
      {customIndicator === false ? (
        <ArrowDropDown style={{ fill: colors.primary }} />
      ) : (
        <ExpandMoreIcon className={classes.icon} />
      )}
    </div>
  );
};

DropdownIndicator.propTypes = {
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
  isDisabled: PropTypes.bool,
};

/**
 * @return {null}
 */
function Menu(props) {
  const { selectProps, children } = props;
  const minSearchParam = selectProps.minSearchCharsLength;
  const { inputValue } = selectProps;
  const isMinSearchValid = inputValue?.length >= minSearchParam;
  const isStaticVisible =
    selectProps.staticOption &&
    inputValue?.length >= 5 &&
    !selectProps.withFiltering &&
    selectProps.options?.length !== 0;

  const menu = (
    <Paper square className={classnames(selectProps.classes.paper, selectProps.dropdownClass)} {...props.innerProps}>
      {selectProps?.optionsDisclaimer}
      {children}
      {isStaticVisible && <div className={selectProps.classes.staticOption}>{selectProps.staticOption}</div>}
    </Paper>
  );
  if (selectProps.isLoading) return null;
  if (isMinSearchValid && !selectProps.startAggregations) {
    return menu;
  }
  return null;
}

Menu.propTypes = {
  children: PropTypes.node.isRequired,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object,
};

const Input = props => {
  const autoComplete = get(props, 'id', uuidv4());
  return <components.Input {...props} autoComplete={autoComplete} />;
};

const overrideComponents = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  ValueContainer,
  ClearIndicator,
  DropdownIndicator,
  Input,
};

const FormikAutocompleteSelect = ({
  t,
  isCreatable,
  isEditable,
  isMulti,
  required,
  label,
  field: { name, value, placeholder },
  form: { errors, touched, setFieldValue, setFieldTouched },
  options,
  minSearchCharsLength,
  customNoResultText,
  customComponents,
  onInputTextChange,
  resetOptions,
  onFocus,
  children,
  onMenuClose,
  onMenuOpen,
  onChange,
  isLoading,
  instanceId,
  inputLabelProps,
  inputProps,
  preventDefaultOnChange,
  withFiltering,
  className,
  overrideMessageStyles,
  styles,
  errorMessageClass,
  blurOnSelect,
  onBlurResetsInput,
  ...other
}) => {
  const SelectRef = React.createRef(null);
  const [inputValue, setInputValue] = React.useState('');
  const classes = useStyles();
  const isMinSearchValid = minSearchValue =>
    (minSearchCharsLength && minSearchValue?.length >= minSearchCharsLength) || !minSearchCharsLength;

  const onUpdateInputValue = value => {
    setInputValue(value.label);
  };

  useEffect(() => {
    if (isCreatable && isEditable) {
      onInputTextChange(value);
    }
    if (isEditable && value) {
      onUpdateInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatable, isEditable, /* onInputTextChange, */ value]);
  // Dont even ask how long it took me to find ^ that part

  const innerOnInputTextChange = (inputInnerValue = '') => {
    const newValue = get(inputInnerValue, 'label', inputInnerValue);
    setInputValue(newValue || '');
    if (newValue === null || newValue.length === 0) {
      const newFieldValue = typeof newValue === 'string' ? { inputInnerValue: null, label: newValue } : newValue;
      setFieldValue(name, newFieldValue);
    }
  };
  const onEditSelectFocus = () => {
    SelectRef.current.state.inputValue = inputValue;
  };
  const onKeyDown = event => {
    if (event.keyCode === 13 && !isLoading) {
      onBlurMenuClose();
    }
  };

  const onBlurMenuClose = () => {
    resetOptions(name);
    if (blurOnSelect) {
      SelectRef.current.blur();
    }
  };

  const formatGroupLabel = data => (
    <div className={classes.groupedLabel}>
      <span>{data.label}</span>
    </div>
  );
  const hasError = Boolean(getIn(touched, name) && getIn(errors, name));
  const errorText = getIn(errors, name);
  const opacityStyle = isEditable ? { opacity: '1 !important' } : {};
  const selectStyles = {
    input: base => ({
      ...base,
      margin: 0,
      // color: selectTextColor || theme.palette.text.primary,
      '& input': {
        ...opacityStyle,
        fontFamily: ['Slate', '"Open Sans"', '"Helvetica Neue"', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      },
    }),
    indicatorSeparator: base => ({
      ...base,
      display: 'none',
    }),
    loadingIndicator: base => ({
      ...base,
      fontSize: 5,
      color: colors.primary,
      '& *': {
        backgroundColor: `${colors.primary} !important`,
        fontSize: 'inherit',
      },
    }),
  };
  const getFilterOption = () => (other.filterOption ? { filterOption: other.filterOption } : {});
  return (
    <FormControl fullWidth error={hasError} className={classnames(classes.field, className)}>
      {isCreatable ? (
        <Creatable
          {...(withFiltering ? getFilterOption() : { filterOption: option => option })}
          instanceId={instanceId}
          classes={classes}
          styles={selectStyles}
          isMulti={isMulti}
          value={value}
          options={options}
          minSearchCharsLength={minSearchCharsLength}
          backspaceRemovesValue
          placeholder={placeholder || ''}
          onMenuOpen={onMenuOpen}
          onChange={val => {
            if (isEditable) {
              innerOnInputTextChange(val);
            }
            if (!preventDefaultOnChange) {
              setFieldValue(name, val);
            }
            onChange(val);
          }}
          formatGroupLabel={formatGroupLabel}
          onFocus={onFocus}
          onBlur={() => setFieldTouched(name, true)}
          components={{ ...overrideComponents, ...customComponents }}
          onInputChange={(val, act) => {
            if (act !== 'input-change') return;
            if (isEditable) {
              innerOnInputTextChange(val);
            }
            if (isMinSearchValid(val)) {
              onInputTextChange(val);
            }
          }}
          onInputTextChange={val => {
            if (isMinSearchValid(val)) {
              onInputTextChange(val);
              if (!val.length) {
                setFieldValue(name, val);
              }
            }
          }}
          customNoResultText={customNoResultText}
          textFieldProps={{
            label,
            inputLabelProps,
            required,
            error: hasError,
            warning: other.warning,
          }}
          name={name}
          formatCreateLabel={inputCreatableValue => (
            <div>
              <span>{t('operabase.general.FN_SELECT_USE')}</span>
              <span className={classes.useText}>{` ${inputCreatableValue}`}</span>
            </div>
          )}
          isLoading={isLoading}
          withFiltering={withFiltering}
          {...removeWithTranslationProps(other)}
        />
      ) : (
        <Select
          ref={SelectRef}
          {...(withFiltering ? getFilterOption() : { filterOption: option => option })}
          instanceId={instanceId}
          classes={classes}
          styles={{ ...selectStyles, ...styles }}
          isMulti={isMulti}
          value={value}
          options={options}
          minSearchCharsLength={minSearchCharsLength}
          backspaceRemovesValue
          onChange={v => {
            if (isEditable) {
              setInputValue(v?.label || '');
            }
            if (!preventDefaultOnChange) {
              setFieldValue(name, v);
            }
            onChange(v);
          }}
          onBlur={val => {
            const newValue = val.target.value;
            setFieldTouched(name, true);
            if (!onBlurResetsInput && newValue.trim().length) {
              setFieldValue(name, { value: '', label: newValue });
            }
          }}
          components={{ ...overrideComponents, ...customComponents }}
          onInputTextChange={val => {
            if (isMinSearchValid(val)) {
              onInputTextChange(val);
            }
          }}
          customNoResultText={customNoResultText}
          onKeyDown={onKeyDown}
          onMenuClose={e => {
            onMenuClose(e);
            if (isEditable) {
              onBlurMenuClose();
            }
          }}
          onMenuOpen={onMenuOpen}
          placeholder={placeholder || ''}
          onFocus={() => {
            if (isEditable && value) {
              onEditSelectFocus();
            }
            if (onFocus) {
              onFocus(value);
            }
          }}
          textFieldProps={{
            label,
            inputLabelProps,
            required,
            error: hasError,
            warning: other.warning,
          }}
          formatGroupLabel={formatGroupLabel}
          name={name}
          isLoading={isLoading}
          {...removeWithTranslationProps(other)}
          withFiltering={withFiltering}
        />
      )}
      {children}
      {errorText && (
        <FormHelperText
          className={classnames(
            overrideMessageStyles || classes.formHelperText,
            {
              [classes.warningColor]: other.warning,
              [classes.errorColor]: hasError,
            },
            errorMessageClass,
          )}
        >
          {/* to set inner values errors of  select options */}
          {isObject(errorText) && !errorText.props ? valuesIn(errorText) : t(errorText)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const selectOptionsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
);

export const selectOptionsWithGroupsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }),
    ),
  }),
);

FormikAutocompleteSelect.propTypes = {
  t: PropTypes.func.isRequired,
  instanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.oneOfType([
    selectOptionsPropType,
    selectOptionsWithGroupsPropType,
    PropTypes.arrayOf(PropTypes.object),
  ]).isRequired,
  children: PropTypes.node,
  minSearchCharsLength: PropTypes.number,
  classes: PropTypes.object,
  customIndicator: PropTypes.bool,
  isCreatable: PropTypes.bool,
  isEditable: PropTypes.bool,
  isClearable: PropTypes.bool,
  theme: PropTypes.object,
  selectTextColor: PropTypes.string,
  isMulti: PropTypes.bool,
  isAsyncSelect: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  customNoResultText: PropTypes.string,
  customComponents: PropTypes.object,
  field: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.object]),
  }),
  form: PropTypes.shape({
    dirty: PropTypes.bool,
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    setFieldTouched: PropTypes.func,
    setFieldValue: PropTypes.func,
  }),
  onChange: PropTypes.func,
  onInputTextChange: PropTypes.func,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onFocus: PropTypes.func,
  isLoading: PropTypes.bool,
  preventDefaultOnChange: PropTypes.bool,
  noIndicator: PropTypes.bool,
  resetOptions: PropTypes.func,
  withFiltering: PropTypes.bool,
  dropdownClass: PropTypes.string,
  staticOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  styles: PropTypes.object,
  optionsDisclaimer: PropTypes.object,
  errorMessageClass: PropTypes.string,
  blurOnSelect: PropTypes.bool,
  onBlurResetsInput: PropTypes.bool,
};

FormikAutocompleteSelect.defaultProps = {
  customIndicator: false,
  isMulti: false,
  isAsyncSelect: false,
  minSearchCharsLength: 0,
  onInputTextChange: () => {},
  onChange: () => {},
  resetOptions: () => {},
  children: undefined,
  form: {
    dirty: false,
    errors: [],
    setFieldValue: () => {},
    setFieldTouched: () => {},
  },
  isLoading: false,
  required: false,
  preventDefaultOnChange: false,
  isEditable: false,
  onFocus: () => {},
  onMenuClose: () => {},
  noIndicator: false,
  withFiltering: false,
  dropdownClass: null,
  styles: {},
  optionsDisclaimer: null,
  blurOnSelect: true,
  onBlurResetsInput: true,
};

export default withTranslation()(FormikAutocompleteSelect);
